import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  Users,
  Shield,
  FileText,
  Key,
  ArrowRight,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api, endpoints } from '@/lib/api-client';
import { useAdmin } from '@/contexts/AdminContext';

export const Route = createFileRoute('/_dashboard/admin/org/')({
  component: OrgAdminDashboard,
});

interface UserStats {
  totalUsers: number;
  byRole: Record<string, number>;
  recentUsers: number;
}

interface AuditSummary {
  totalEvents: number;
  recentActivity: Array<{
    id: string;
    action: string;
    userEmail?: string;
    createdAt: string;
  }>;
}

function OrgAdminDashboard() {
  const { adminContext } = useAdmin();
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [auditSummary, setAuditSummary] = useState<AuditSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, auditData] = await Promise.all([
          api.get<UserStats>(endpoints.adminUsersStats),
          api.get<AuditSummary>(endpoints.adminAuditSummary),
        ]);
        setUserStats(statsData);
        setAuditSummary(auditData);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Organization Dashboard</h1>
        <p className="text-muted-foreground">
          Manage your organization's users, roles, and policies
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Team Members</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{userStats?.totalUsers || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              +{userStats?.recentUsers || 0} this week
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Admins</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(userStats?.byRole?.admin || 0) + (userStats?.byRole?.owner || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Organization admins</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Audit Events</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{auditSummary?.totalEvents || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Logged actions</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Members</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{userStats?.byRole?.member || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Regular members</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common organization management tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Button variant="outline" className="h-auto py-4 flex-col gap-2" asChild>
              <Link to="/admin/org/users">
                <Users className="h-5 w-5" />
                <span>Manage Members</span>
              </Link>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex-col gap-2" asChild>
              <Link to="/admin/org/roles">
                <Shield className="h-5 w-5" />
                <span>View Roles</span>
              </Link>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex-col gap-2" asChild>
              <Link to="/admin/org/policies">
                <FileText className="h-5 w-5" />
                <span>View Policies</span>
              </Link>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex-col gap-2" asChild>
              <Link to="/admin/org/api-keys">
                <Key className="h-5 w-5" />
                <span>API Keys</span>
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Latest actions in your organization</CardDescription>
            </div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/admin/audit">
                View All <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {auditSummary?.recentActivity?.slice(0, 5).map((event) => (
              <div key={event.id} className="flex items-center justify-between text-sm">
                <div className="flex flex-col">
                  <span className="font-medium">{event.action}</span>
                  <span className="text-xs text-muted-foreground">
                    {event.userEmail || 'Unknown user'}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {new Date(event.createdAt).toLocaleString()}
                </span>
              </div>
            ))}
            {(!auditSummary?.recentActivity || auditSummary.recentActivity.length === 0) && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No recent activity
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
